import { Injectable, OnDestroy, ProviderToken } from '@angular/core';
import { DrawingModeEnum } from '../../../../zones/models/drawing-mode.enum';
import { MapServiceInterface } from './mapServiceInterface';
import { MapPopupInterface } from '../models/map-popup.interface';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { AppInjector } from '../../../../core/app-injector';
import { StorageService } from '../../../../core/storage/storage.service';
import jwt_decode from 'jwt-decode';
import { TagEnum } from '../../../../settings/models/tag.enum';
import { MapEnum } from '../models/map.enum';
import { PrivilegesEnum } from '../../../../shared/models/privileges.enum';
import { Coordinates } from '../../../../units/models/coordinates';

@Injectable({
  providedIn: 'root'
})
export class MapFacade {
  private _subscriptions: Subscription = new Subscription();
  private _mapService: MapServiceInterface;
  private _mapSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  private _mapProvider: BehaviorSubject<MapEnum> = new BehaviorSubject<MapEnum>(null);
  private _isHiddenMapSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private _pinnedUnitSubject: BehaviorSubject<number> = new BehaviorSubject<number>(null);
  public provider$: Observable<MapEnum> = this._mapProvider.asObservable();
  public pinnedUnit$: Observable<number> = this._pinnedUnitSubject.asObservable();

  constructor(
    private storage: StorageService,
  ) {
  }

  public init() {
    const currentUser = this.storage.get('currentUser');
    const decodedToken = jwt_decode(currentUser?.accessToken);
    const settings: any = JSON.parse(decodedToken['settings']);
    const modules: any = JSON.parse(decodedToken['teagleVisability']);
    if (settings && modules) {
      const googleMapsEnabled = modules?.find(privilege => privilege === PrivilegesEnum.TeagleMapGoogleMaps);
      const mapboxEnabled = modules?.find(privilege => privilege === PrivilegesEnum.TeagleMapMapBox);
      let provider = settings[TagEnum.MapProvider.name] || MapEnum.OSM;
      if ((provider === MapEnum.GOOGLE && !googleMapsEnabled) || (provider === MapEnum.MAPBOX && !mapboxEnabled)) {
        provider = MapEnum.OSM;
      }
      this._mapProvider.next(provider);

    }
  }

  public injectProvider(token: ProviderToken<any>) {
    if (!token) { return; }
    const injector = AppInjector.getInjector();
    this._mapService = injector.get(token);
    if (this._mapService) {
      if (!this._mapService.getMap()) {
        this._mapService.setMap(this._mapSubject.getValue());
      }
      this._subscriptions.add(this._mapService.map.subscribe(map => this._mapSubject.next(map)));

    }
  }

  public destroy() {
    this._subscriptions.unsubscribe();
    this._mapProvider.next(null);
    this._mapSubject.next(null);
    this._mapService = null;
  }

  public get provider(): MapEnum {
    return this._mapProvider.value;
  }

  public get areGoogleMaps(): boolean {
    return this.provider === MapEnum.GOOGLE;
  }

  public get removeAll$(): Observable<boolean> {
    return this._mapService?.removeAll$;
  }

  public get map() {
    return this._mapSubject?.asObservable();
  }


  public getMap() {
    return this._mapService?.getMap();
  }

  public setMap(map: any) {
    this._mapService?.setMap(map);
    this._mapSubject.next(map);
  }

  public getMapZoom() {
    return this._mapService?.getMapZoom();
  }

  public setMaxZoom(maxZoom: number) {
    this._mapService?.setMaxZoom(maxZoom);
  }

  public clearMap(options?: any) {
    this._mapService?.clearMap(options);
  }

  public resizeMap() {
    this._mapService?.resizeMap();
  }

  public addMapEventListener(event: any, listener: (e: any) => void, layer?: string) {
    this._mapService?.addMapEventListener(event, listener, layer);
  }

  public addMapEventListenerOnce(event: string, listener: (e: any) => void) {
    this._mapService?.addMapEventListenerOnce(event, listener);
  }

  public setMapZoom(data: {zoom: number, coordinates?: number[], offset?: number}) {
    this._mapService?.setMapZoom(data);
  }

  public calculateOffset (width: number) {
    return this._mapService?.calculateOffset(width);
  }

  public getBounds() {
    return this._mapService?.getBounds();
  }

  public isInBounds(point, bounds) {
    return this._mapService?.isInBounds(point, bounds);
  }

  public fitBounds(bounds, options?) {
    this._mapService?.fitBounds(bounds, options);
  }

  public panBy(x: number, y: number) {
    this._mapService?.panBy(x, y);
  }

  public setBoundOffset(offset: number) {
    this._mapService?.setBoundOffset(offset);
  }

  public getBoundsFromGeoJson(geoJson: any): any {
   return this._mapService?.getBoundsFromGeoJson(geoJson);
  }

  public getBoundsFromCoordinates(coordinates: any): any {
    return this._mapService?.getBoundsFromCoordinates(coordinates);
  }
  public haversineDistance(bounds: any): any {
    return this._mapService?.haversineDistance(bounds);
  }

  public getBoundsFromFeatures(features: any): any {
    return this._mapService?.getBoundsFromFeatures(features);
  }

  public getClusters(geoJsonFuture, options?, onlyMarkersAndClusters?: boolean): Promise<any> {
    return this._mapService?.getClusters(geoJsonFuture, options, onlyMarkersAndClusters);
  }

  public getLeaves(clusterId) {
    return this._mapService?.getLeaves(clusterId);
  }

  public removePopups(className?: string) {
    this._mapService?.removePopups(className);
  }

  public removeMarkers(className?: string) {
    this._mapService?.removeMarkers(className);
  }

  public createMarker(el: any, coords: any, popup: any, id?: any) {
    return this._mapService?.createMarker(el, coords, popup, id);
  }

  public createSimpleMarker(coords: Coordinates, map: any, id?: any): any {
    return this._mapService.createSimpleMarker(coords, map, id);
  }

  public createPopup(popupContent: any, coords: any, id?: any, options?, onOpen?: (e: any) => void, onClose?: (e: any) => void): MapPopupInterface {
    return this._mapService?.createPopup(popupContent, coords, id, options, onOpen, onClose);
  }

  public getTilt(): number {
    return this._mapService?.getTilt();
  }

  public setTilt(amount: number): void {
    this._mapService?.setTilt(amount);
  }

  public getCurrentRotation(): number {
    return this._mapService?.getCurrentRotation();
  }

  public computeRotation(from: number[], to: number[]): number {
    return this._mapService?.computeRotation(from, to);
  }

  public rotateTo(amount: number): void {
    this._mapService?.rotateTo(amount);
  }

  public addSource(name: string, geojson) {
    this._mapService?.addSource(name, geojson);
  }

  public updateSource(name: string, geojson) {
    this._mapService?.updateSource(name, geojson);
  }

  public addLayer(options: any) {
    this._mapService?.addLayer(options);
  }

  public getSource(name: string) {
    return this._mapService?.getSource(name);
  }

  public getLayer(name: string) {
    return this._mapService?.getLayer(name);
  }

  public removeLayers(layers: string[]) {
    this._mapService?.removeLayers(layers);
  }

  public removeSources(sources: string[]) {
    this._mapService?.removeSources(sources);
  }

  public queryRenderedFeatures(layerName: string, point: any, layers: any) {
    return this._mapService?.queryRenderedFeatures(layerName, point, layers);
  }

  public setCanvasCursorStyle(style: string) {
    this._mapService?.setCanvasCursorStyle(style);
  }

  public setPaintProperty(layer: string, name: string, value: any, klass?: string) {
    this._mapService?.setPaintProperty(layer, name, value, klass);
  }

  public setFilter(layer: string, filter?: any) {
    this._mapService?.setFilter(layer, filter);
  }

  public setDrawingManager() {
    return this._mapService?.setDrawingManager();
  }

  public changeDrawingMode(mode: DrawingModeEnum, onSetDrawingManger: () => void) {
    this._mapService?.changeDrawingMode(mode, onSetDrawingManger);
  }

  public changeDrawingModeToSelect(mode: DrawingModeEnum, featureIds: number[]) {
    this._mapService?.changeDrawingModeToSelect(mode, featureIds);
  }

  public setFeaturesToDrawingManager(features) {
    this._mapService?.setFeaturesToDrawingManager(features);
  }

  public deleteAllDrawingItems() {
    this._mapService?.deleteAllDrawingItems();
  }

  public isDrawingManager() {
    return this._mapService?.isDrawingManager();
  }

  public getDrawingManagerMode() {
    return this._mapService?.getDrawingManagerMode();
  }

  public removeUnusedMarkers(classNames: string[]): void {
    this._mapService?.removeUnusedMarkers(classNames);
  }

  public panTo(coordinates: number[]): void {
    this._mapService?.panTo(coordinates);
  }
  
  public deleteAllPolylines(): void {
    this._mapService?.deleteAllPolylines();
  }

  public getPolylinesLength(): Observable<number> {
    return this._mapService?.getPolylinesLength();
  }
  
  public isHiddenMap(): Observable<boolean> {
    return this._isHiddenMapSubject.asObservable();
  }
  public setIsHiddenMap(isHidden: boolean) {
    this._isHiddenMapSubject.next(isHidden);
  }

  public getBoundOffset(): Observable<number> {
    return this._mapService?.getBoundOffset();
  }

  public zoomMap(): Observable<{zoom: number, coordinates?: number[], offset?: number}> {
    return this._mapService?.zoomMap();
  }

  public get clearMapObservable(): Observable<any> {
    return this._mapService?.clearMapObservable;
  }

  public pinUnit(unitId: number) {
    if (this._pinnedUnitSubject.getValue() !== unitId) {
      this._pinnedUnitSubject.next(unitId);
    } else {
      this._pinnedUnitSubject.next(null);
    }
  }

  public setVisibilityToolsForTrackViewer(visible: boolean): void{
    this._mapService?.setVisibilityToolsForTrackViewer(visible);
  }

  public setVisibilityToolsForZones(visible: boolean): void{
    this._mapService?.setVisibilityToolsForZones(visible);
  }
}
